import { Link } from "react-router-dom";
import { ArrowLeft, ChevronRight, Phone, Mail } from "lucide-react";
import { COMPANY } from "@/lib/config";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

/**
 * Reusable layout for pillar / expert content pages.
 * Renders: header, breadcrumb, hero with cover image, article body, CTA, footer.
 * Auto-injects Article + BreadcrumbList Schema.org data for SEO.
 */
export default function ArticleLayout({
    slug,
    title,
    metaTitle,
    subtitle,
    coverImage,
    coverAlt,
    breadcrumbLabel,
    metaDescription,
    updated,
    children,
}) {
    const url = `https://riffet.fr/${slug}`;
    const schemaTitle = metaTitle || (typeof title === "string" ? title : breadcrumbLabel);

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: "https://riffet.fr/" },
            { "@type": "ListItem", position: 2, name: "Expertise", item: "https://riffet.fr/#expertise" },
            { "@type": "ListItem", position: 3, name: breadcrumbLabel, item: url },
        ],
    };

    const articleSchema = {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: schemaTitle,
        description: metaDescription,
        image: coverImage,
        author: { "@type": "Organization", name: COMPANY.name, url: "https://riffet.fr/" },
        publisher: {
            "@type": "Organization",
            name: COMPANY.name,
            logo: { "@type": "ImageObject", url: "https://riffet.fr/favicon.ico" },
        },
        datePublished: "2026-01-01",
        dateModified: updated || "2026-01-01",
        mainEntityOfPage: url,
    };

    return (
        <main className="bg-cream text-ink min-h-screen" data-testid={`article-${slug}`}>
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

            <Header />

            {/* Cover */}
            <section className="relative pt-24 md:pt-32 pb-12 md:pb-16 overflow-hidden">
                <div className="absolute inset-0 grain-bg pointer-events-none" />
                <div className="max-w-6xl mx-auto px-6 md:px-10">
                    {/* Breadcrumb */}
                    <nav aria-label="Fil d'Ariane" className="mb-10">
                        <ol className="flex items-center gap-2 text-xs text-stone2" itemScope itemType="https://schema.org/BreadcrumbList">
                            <li className="flex items-center gap-2" itemScope itemType="https://schema.org/ListItem">
                                <Link to="/" className="hover:text-brand" itemProp="item">
                                    <span itemProp="name">Accueil</span>
                                </Link>
                                <ChevronRight size={12} />
                            </li>
                            <li className="flex items-center gap-2">
                                <Link to="/#expertise" className="hover:text-brand">Expertise</Link>
                                <ChevronRight size={12} />
                            </li>
                            <li className="text-ink/70 font-medium">{breadcrumbLabel}</li>
                        </ol>
                    </nav>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                        <div className="lg:col-span-7">
                            <span className="label-overline text-brand">Expertise · Bâti ancien</span>
                            <h1 className="mt-4 font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter text-ink leading-[1.05]">
                                {title}
                            </h1>
                            {subtitle && (
                                <p className="mt-6 text-base md:text-lg text-stone2 leading-relaxed max-w-xl">
                                    {subtitle}
                                </p>
                            )}
                        </div>
                        {coverImage && (
                            <div className="lg:col-span-5">
                                <div className="aspect-[4/3] overflow-hidden rounded-sm border border-border">
                                    <img
                                        src={coverImage}
                                        alt={coverAlt || title}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Body */}
            <article className="max-w-3xl mx-auto px-6 md:px-10 pb-16 md:pb-24">
                <div className="prose-article space-y-6 text-ink/85 text-base md:text-[17px] leading-relaxed">
                    {children}
                </div>
            </article>

            {/* CTA */}
            <section className="bg-ink text-cream py-16 md:py-20">
                <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
                    <span className="label-overline text-brand">Un projet, un désordre, un doute ?</span>
                    <h2 className="mt-4 font-heading text-3xl md:text-4xl font-light tracking-tight leading-tight">
                        Parlons-en. <span className="italic">Un avis clair, sans engagement.</span>
                    </h2>
                    <div className="mt-8 flex flex-wrap justify-center gap-4">
                        <a
                            href={`tel:${COMPANY.phoneRaw}`}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-brand hover:bg-brand-hover text-cream rounded-sm font-medium"
                        >
                            <Phone size={16} /> {COMPANY.phone}
                        </a>
                        <Link
                            to="/#contact"
                            className="inline-flex items-center gap-2 px-6 py-3 border border-cream/25 hover:border-brand text-cream rounded-sm"
                        >
                            <Mail size={16} /> Écrire un message
                        </Link>
                    </div>
                </div>
            </section>

            <Footer />
        </main>
    );
}

/* Article helper components (typography) */
export function H2({ children }) {
    return <h2 className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-ink mt-12 mb-4 leading-snug">{children}</h2>;
}
export function H3({ children }) {
    return <h3 className="font-heading text-xl font-medium tracking-tight text-ink mt-8 mb-3">{children}</h3>;
}
export function Lead({ children }) {
    return <p className="text-lg md:text-xl text-ink/80 leading-relaxed font-light">{children}</p>;
}
export function Note({ children, tone = "brand" }) {
    const cls = tone === "brand"
        ? "border-brand/40 bg-brand/5"
        : "border-border bg-sand/30";
    return (
        <aside className={`border-l-4 ${cls} pl-5 py-4 my-6 text-ink/80 rounded-r-sm`}>
            {children}
        </aside>
    );
}
export function BackHome() {
    return (
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-stone2 hover:text-brand mt-10">
            <ArrowLeft size={14} /> Retour à l'accueil
        </Link>
    );
}
